import { useCallback } from 'react';
import type { AccountSettings, AccountSettingsPatchRequest } from '@sorrem/shared';
import { applyOptimistic, sorremApi, useAppDispatch, useGetAccountSettingsQuery, type RootState } from '@sorrem-client/ui/store';
import { getStoredCanonicalAccountId } from '@sorrem-client/ui/shared/auth-session';

/** The account settings a player can change. */
export type AccountSettingKey = keyof AccountSettingsPatchRequest & keyof AccountSettings;

export interface AccountSettingState<K extends AccountSettingKey> {
    /** The saved value; \`fallback\` until the settings load. */
    value: AccountSettings[K];
    /** Whether the account's settings have loaded. */
    isLoaded: boolean;
    /**
     * Saves a new value, or one derived from the latest (applied at once,
     * undone if the server refuses). An updater that returns the value it
     * was given saves nothing. Before the settings load nothing is saved,
     * so a value built on the defaults never overwrites the account's.
     */
    set: (next: AccountSettings[K] | ((current: AccountSettings[K]) => AccountSettings[K])) => void;
}

/**
 * One account setting: read it, and save changes to it. Each change is built
 * on the cached settings as they are when it is made, so quick changes in a
 * row (two panel toggles before a render) all land.
 */
export function useAccountSetting<K extends AccountSettingKey>(key: K, fallback: AccountSettings[K]): AccountSettingState<K> {
    const accountId = getStoredCanonicalAccountId();
    const dispatch = useAppDispatch();
    const { data: settings } = useGetAccountSettingsQuery(accountId ?? '', { skip: accountId === null });

    const set = useCallback((next: AccountSettings[K] | ((current: AccountSettings[K]) => AccountSettings[K])): void => {
        if (accountId === null) {
            return;
        }
        dispatch((_dispatch, getState) => {
            const cached = sorremApi.endpoints.getAccountSettings.select(accountId)(getState() as RootState).data;
            if (cached === undefined) {
                return;
            }
            // Settings loaded from a server older than this key lack it: build on the fallback, as `value` reads.
            const current = cached[key] ?? fallback;
            const value = typeof next === 'function' ? (next as (current: AccountSettings[K]) => AccountSettings[K])(current) : next;
            if (Object.is(value, current)) {
                return;
            }
            // Into the cache now, not when the request starts a tick later, so the next change builds on it.
            const applied = dispatch(sorremApi.util.updateQueryData('getAccountSettings', accountId, (draft) => {
                Object.assign(draft, { [key]: value });
            }));
            void applyOptimistic(applied, dispatch(sorremApi.endpoints.patchAccountSettings.initiate({ accountId, body: { [key]: value } })).unwrap());
        });
    }, [accountId, dispatch, key, fallback]);

    return { value: settings?.[key] ?? fallback, isLoaded: settings !== undefined, set };
}
